<script lang="ts">
// eslint-disable-next-line @typescript-eslint/naming-convention
const Operator = {
  '=': 'equals',
  '≤': 'lessThanOrEqual',
  '≥': 'greaterThanOrEqual',
  '<': 'lessThan',
  '>': 'greaterThan',
} as const;
type Operator = (typeof Operator)[keyof typeof Operator];

interface FilterModel {
  filter: bigint;
  type: Operator;
}

export interface LocalFilterModel {
  filter: bigint | '';
  type: Operator;
}
</script>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeMount, ref, shallowRef, watch } from 'vue';
import { IFloatingFilterParams } from 'ag-grid-enterprise';
import Wrapper from './FilterWrapper.vue';
import InputFormattedNumber from './InputFormattedNumber.vue';

const localFilterModel = ref<LocalFilterModel>({ filter: '', type: Operator['='] });
const filterValue = computed({
  get: () => localFilterModel.value.filter.toString(),
  set: (value) => {
    localFilterModel.value.filter = value === '' ? '' : BigInt(value);
  },
});

const params = shallowRef<IFloatingFilterParams & { filterParams: { allowNegative?: boolean } }>();

defineExpose({
  onParentModelChanged: updateLocalFilter,
});

onBeforeMount(() => {
  params.value = getCurrentInstance()?.proxy.$data.params;
  if (params.value === undefined) return;

  const filterModel = params.value.api.getColumnFilterModel<FilterModel>(params.value.column);
  updateLocalFilter(filterModel);
});

function updateLocalFilter(filterModel: FilterModel | null) {
  if (filterModel === null) {
    localFilterModel.value.type = Operator['='];
    localFilterModel.value.filter = '';
  } else {
    localFilterModel.value = filterModel;
  }
}

watch(localFilterModel, applyFilter, { deep: true, immediate: true });

async function applyFilter() {
  if (params.value === undefined) return;
  await params.value.api.setColumnFilterModel<FilterModel>(
    params.value.column,
    localFilterModel.value.filter === '' ? null : (localFilterModel.value as FilterModel)
  );
  params.value.api.onFilterChanged();
}
</script>

<template>
  <Wrapper :data-test-filter="params?.column.getColId()">
    <select
      v-model="localFilterModel.type"
      class="ag-input-field-input ag-text-field-input"
      style="width: 20px; text-align: center"
    >
      <option v-for="(value, text) in Operator" :key="text" :value="value">{{ text }}</option>
    </select>
    <InputFormattedNumber
      v-model="filterValue"
      :allow-negative="params?.filterParams.allowNegative ?? true"
      autocomplete="off"
      class="ag-input-field-input ag-text-field-input"
    />
  </Wrapper>
</template>
